import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSpring, animated, config } from 'react-spring';
import '../styles/Experience.css';
import { LOCALES, localizeText } from '../utils/localize';

const assetPath = (path) => `${process.env.PUBLIC_URL}${encodeURI(path)}`;

const experiences = [
  {
    id: 1,
    company: 'LIG D&A',
    position: 'Research Engineer',
    period: '2025.07 - Present',
    description: '실무 환경에서 제품 소프트웨어 설계 & 개발 & 검증까지 전반적인 개발을 통해 무기체계 연구 및 개발 진행',
    achievements: ['소프트웨어 개발 프로세스인 V모델을 활용한 체계 종합 연구', '실시간 처리가 필요한 시스템 소프트웨어 개발', '다양한 업체와의 협업을 통한 제품 완성도 상향'],
  },
  {
    id: 2,
    company: '삼성청년소프트웨어 아카데미 (SSAFY)',
    position: 'Embedded Track',
    period: '2024.08 - 2025.07',
    description: '임베디드 & 펌웨어 개발 역량 강화 및 팀 프로젝트 주도',
    achievements: [
      '최우수(상위 5%) 수료',
      'S/W 역량테스트 Professional 등급 달성',
      'The Lee, Javix, Leafree 팀 프로젝트 설계 및 구현',
    ],
    evidenceTitle: 'SSAFY 인증서',
    evidenceDescription: '삼성청년소프트웨어아카데미(SSAFY) Embedded Track 수료/인증 자료',
    evidence: [
      { src: assetPath('/award/SSAFY 인증서.jpg'), label: 'SSAFY 인증서', tags: ['SSAFY', '인증서', 'Embedded'] },
      { src: assetPath('/award/SSAFY인증서2.jpg'), label: 'SSAFY 인증서 (추가)', tags: ['SSAFY', '인증서', '추가본'] },
    ],
  },
  {
    id: 3,
    company: 'Human-centered Mobile Systems Lab',
    position: 'Undergraduate Research Intern',
    period: '2023.06 - 2024.06',
    description: '실시간 시스템 환경에서 Edge TPU 기반 Embedded AI 연구를 수행',
    achievements: [
      'Coral Dev Board의 Driver Library 개별화 연구',
      'Memory-Efficient Pipelined Parallelism in Edge TPUs on Real-time System 연구',
      'Edge TPU 기반 On-device AI 구조 이해 및 추론 최적화 연구',
    ],
  },
  {
    id: 4,
    company: 'Google Developer Student Club (GDSC)',
    position: '1기 Member',
    period: '2022.03 - 2022.12',
    description: 'Google Developer Student Club 1기 멤버로 개발 커뮤니티 활동에 참여했습니다.',
    achievements: ['개발 스터디 및 커뮤니티 활동 참여', '동료 개발자와 기술 학습 및 프로젝트 경험 공유'],
  },
  {
    id: 5,
    company: '아주대학교',
    position: 'B.S. in Digital Media & Software Engineering',
    period: '2019.03 - 2024.08',
    description: '디지털미디어학과를 주전공으로, 소프트웨어학과를 복수전공으로 이수했습니다.',
    achievements: ['졸업 석차 5등으로 전공 학습과 프로젝트 수행 역량을 입증', '소프트웨어 이론 및 실무 학습', 'OpenCV, OpenGL을 통한 렌더링 알고리즘 학습 및 구현', '다양한 팀 프로그래밍 프로젝트 수행'],
  },
];

const credentials = [
  {
    id: 'award-khuthon-2024',
    label: '수상',
    type: 'award',
    title: 'KHUTON 대상',
    date: '2024.05',
    description: '실시간 고속도로 혼잡료 요금 부과 시스템',
    tags: ['Hackathon', '대상', '실시간 시스템'],
    evidence: [
      { src: assetPath('/award/2024_khuthon_대상.png'), label: '2024 KHUTON 대상 상장', tags: ['KHUTON', '대상', '상장'] },
    ],
  },
  {
    id: 'award-softcon-2024',
    label: '수상',
    type: 'award',
    title: 'SOFTCON 우수상',
    date: '2024.06',
    description: '실시간 차량 디지털 트윈 시뮬레이션',
    tags: ['SOFTCON', '우수상', 'DigitalTwin'],
    evidence: [
      { src: assetPath('/award/24_소프트콘_우수상.jpeg'), label: '2024 SOFTCON 우수상 상장', tags: ['SOFTCON', '우수상', '상장'] },
    ],
  },
  {
    id: 'award-javix-2025',
    label: '수상',
    type: 'award',
    title: '삼성전자 연계프로젝트 우수상 3등',
    date: '2025.04.11',
    description: '멀티 디바이스 음성 이벤트 통합 비서 플랫폼 Javix',
    tags: ['Javix', '우수상', '3등'],
    evidence: [
      { src: assetPath('/award/Javix 우수상.jpg'), label: 'Javix 우수상 3등 상장', tags: ['Javix', '우수상', '3등'] },
    ],
  },
  {
    id: 'award-leafree-2025',
    label: '수상',
    type: 'award',
    title: 'SSAFY 자율프로젝트 우수상 1등',
    date: '2025.05.22',
    description: '스마트팜 자동제어/음성 인터랙션 기반 Leafree 프로젝트',
    tags: ['Leafree', '우수상', '1등'],
    evidence: [
      { src: assetPath('/award/leafree 우수상.jpg'), label: 'Leafree 우수상 1등 상장', tags: ['Leafree', '우수상', '1등'] },
    ],
  },
  {
    id: 'cert-samsung-professional',
    label: '자격',
    type: 'certification',
    title: '삼성 SW 역량테스트 Professional',
    date: '2024.09.20 취득',
    description: '삼성 SW 역량테스트 Professional 등급 취득',
    validity: '유효기간: 2024.09.20 ~ 2026.09.20',
    tags: ['Samsung', 'Professional', 'Algorithm'],
    evidence: [
      { src: assetPath('/award/samsung 역량 시험 합격 메일.png'), label: '합격 메일', tags: ['Samsung', '합격', '메일'] },
      { src: assetPath('/award/samsung 역량 시험 자격현황.png'), label: '자격 현황', tags: ['Samsung', 'Professional', '자격현황'] },
    ],
  },
];

const toHashTag = (value) => `#${String(value ?? '').replace(/\s+/g, '')}`;

const Experience = ({ locale = LOCALES.KR }) => {
  const L = (text) => localizeText(text, locale);
  const isEnglish = locale === LOCALES.EN;
  const [expandedId, setExpandedId] = useState(null);
  const [expandedCredentialId, setExpandedCredentialId] = useState(null);
  const [selectedProof, setSelectedProof] = useState(null);

  useEffect(() => {
    if (!selectedProof) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setSelectedProof(null);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [selectedProof]);

  const containerSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: config.slow,
  });

  return (
    <section className="experience" id="experience">
      <animated.div style={containerSpring} className="experience-container section-shell">
        <div className="section-header experience-section-header">
          <h2 className="section-title">About Me</h2>
        </div>
        <div className="experience-meta-pills" aria-hidden="true">
          <span>{isEnglish ? `Career · Experience ${experiences.length}` : `경력 · 경험 ${experiences.length}건`}</span>
          <span>{isEnglish ? `Awards · Certifications ${credentials.length}` : `수상 · 자격 ${credentials.length}건`}</span>
        </div>
        <div className="experience-content-split">
          <section className="experience-pane">
            <div className="experience-pane-head">
              <p className="experience-pane-eyebrow">Experience</p>
              <h3>{L('경력 / 경험')}</h3>
            </div>
            <div className="experience-list">
              {experiences.map((exp) => (
                <ExperienceCard
                  key={exp.id}
                  experience={exp}
                  locale={locale}
                  isExpanded={expandedId === exp.id}
                  onToggle={() => setExpandedId(expandedId === exp.id ? null : exp.id)}
                  isProofOpen={selectedProof?.proofId === `exp-${exp.id}`}
                  onToggleProof={exp.evidence
                    ? () => setSelectedProof(selectedProof?.proofId === `exp-${exp.id}` ? null : {
                      proofId: `exp-${exp.id}`,
                      label: '경력',
                      title: exp.evidenceTitle ?? exp.company,
                      date: exp.period,
                      description: exp.evidenceDescription ?? exp.description,
                      evidence: exp.evidence,
                    })
                    : undefined}
                />
              ))}
            </div>
          </section>
          <section className="experience-pane">
            <div className="experience-pane-head">
              <p className="experience-pane-eyebrow">Record</p>
              <h3>{L('수상 / 자격 인증')}</h3>
            </div>
            <div className="experience-credential-list">
              {credentials.map((credential) => (
                <CredentialRecord
                  key={credential.id}
                  credential={credential}
                  locale={locale}
                  isExpanded={expandedCredentialId === credential.id}
                  onToggle={() => setExpandedCredentialId(expandedCredentialId === credential.id ? null : credential.id)}
                  isProofOpen={selectedProof?.proofId === credential.id}
                  onToggleProof={() => setSelectedProof(selectedProof?.proofId === credential.id ? null : { ...credential, proofId: credential.id })}
                />
              ))}
            </div>
          </section>
        </div>
      </animated.div>
      {selectedProof && typeof document !== 'undefined'
        ? createPortal(
          <div className="experience-proof-overlay" role="dialog" aria-modal="true" aria-labelledby="experience-proof-title">
            <button
              type="button"
              className="experience-proof-backdrop"
              aria-label="Close evidence"
              onClick={() => setSelectedProof(null)}
            />
            <div className="experience-proof-panel">
              <div className="experience-proof-head">
                <div>
                  <p className="experience-proof-label">{L(selectedProof.label)}</p>
                  <h3 id="experience-proof-title">{L(selectedProof.title)}</h3>
                  <p className="experience-proof-meta">{selectedProof.date}</p>
                  {selectedProof.validity && <p className="experience-proof-validity">{L(selectedProof.validity)}</p>}
                </div>
                <button type="button" className="experience-proof-close" onClick={() => setSelectedProof(null)}>
                  {L('닫기')}
                </button>
              </div>
              <p className="experience-proof-description">{L(selectedProof.description)}</p>
              <div className={`experience-proof-gallery ${selectedProof.evidence.length === 1 ? 'is-single' : ''}`}>
                {selectedProof.evidence.map((asset) => (
                  <figure key={`${selectedProof.title}-${asset.src}`} className="experience-proof-card">
                    <img src={asset.src} alt={`${L(selectedProof.title)} ${L(asset.label)}`} />
                    <figcaption className="experience-proof-tags">
                      {(asset.tags ?? [asset.label]).map((tag) => (
                        <span key={`${selectedProof.title}-${asset.src}-${tag}`}>{toHashTag(L(tag))}</span>
                      ))}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )
        : null}
    </section>
  );
};

const ExperienceCard = ({ experience, locale = LOCALES.KR, isExpanded, onToggle, isProofOpen, onToggleProof }) => {
  const L = (text) => localizeText(text, locale);
  const contentSpring = useSpring({
    maxHeight: isExpanded ? '520px' : '0px',
    opacity: isExpanded ? 1 : 0,
    transform: isExpanded ? 'translateY(0)' : 'translateY(-10px)',
    config: { ...config.default, tension: 280, friction: 60 },
  });

  return (
    <article
      className="experience-card"
    >
      <button type="button" className="experience-header" onClick={onToggle} aria-expanded={isExpanded}>
        <div className="experience-info">
          <h3 className="experience-company">{L(experience.company)}</h3>
          <p className="experience-position">{L(experience.position)}</p>
          <p className="experience-period">{experience.period}</p>
        </div>
        <span className={`toggle-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
      </button>

      <animated.div style={contentSpring} className="experience-details">
        <p className="experience-description">{L(experience.description)}</p>
        <div className="experience-achievements">
          <h4>Highlights</h4>
          <ul>
            {experience.achievements.map((achievement, idx) => (
              <li key={idx}>{L(achievement)}</li>
            ))}
          </ul>
        </div>
        {onToggleProof && (
          <div className="experience-evidence-actions">
            <ProofTrigger label={L('더보기')} onClick={onToggleProof} isExpanded={isProofOpen} />
          </div>
        )}
      </animated.div>
    </article>
  );
};

const CredentialRecord = ({ credential, locale = LOCALES.KR, isExpanded, onToggle, isProofOpen, onToggleProof }) => {
  const L = (text) => localizeText(text, locale);
  const contentSpring = useSpring({
    maxHeight: isExpanded ? '280px' : '0px',
    opacity: isExpanded ? 1 : 0,
    transform: isExpanded ? 'translateY(0)' : 'translateY(-8px)',
    config: { ...config.default, tension: 280, friction: 60 },
  });

  return (
    <article className="experience-credential-item">
      <button type="button" className="experience-credential-item-head" onClick={onToggle} aria-expanded={isExpanded}>
        <div className="experience-credential-main">
          <div className="experience-credential-top">
            <span className={`experience-credential-kind is-${credential.type}`}>{L(credential.label)}</span>
            <span className="experience-credential-date">{credential.date}</span>
          </div>
          <h4 className="experience-credential-title">{L(credential.title)}</h4>
        </div>
        <span className={`experience-credential-toggle ${isExpanded ? 'expanded' : ''}`}>▼</span>
      </button>
      <animated.div style={contentSpring} className="experience-credential-body">
        <div className="experience-credential-body-inner">
          <p className="experience-credential-description">{L(credential.description)}</p>
          {credential.validity && <p className="experience-credential-validity">{L(credential.validity)}</p>}
          <div className="experience-credential-foot">
            <div className="experience-credential-tags">
              {credential.tags.map((tag) => (
                <span key={`${credential.id}-${tag}`}>{toHashTag(L(tag))}</span>
              ))}
            </div>
            <ProofTrigger label={L('더보기')} onClick={onToggleProof} isExpanded={isProofOpen} />
          </div>
        </div>
      </animated.div>
    </article>
  );
};

const ProofTrigger = ({ label, onClick, isExpanded = false }) => (
  <button type="button" className={`experience-proof-trigger ${isExpanded ? 'expanded' : ''}`} onClick={onClick}>
    <span className="experience-proof-trigger-plus" aria-hidden="true">
      <img src={`${process.env.PUBLIC_URL}/icons/archive-plus.svg`} alt="" />
    </span>
    <span className="experience-proof-trigger-text">{label}</span>
  </button>
);

export default Experience;
